import { useCallback, useEffect, useState } from "react";
import {
  api,
  type HourlyMetric,
  type TopItem,
  type PlatformAvgCheck,
  type DailyMetric,
  type ReconciliationRow,
} from "./api";
import { useTranslation } from "./i18n";

type Props = {
  restaurantId: string;
};

const PLATFORM_COLORS: Record<string, string> = {
  wolt: "#009de0",
  uber_eats: "#06c167",
  glovo: "#ffc244",
  pyszne: "#ff8000",
  bolt_food: "#34d186",
};

const PLATFORM_LABELS: Record<string, string> = {
  wolt: "Wolt",
  uber_eats: "Uber Eats",
  glovo: "Glovo",
  pyszne: "Pyszne.pl",
  bolt_food: "Bolt Food",
};

function fmt(minor: number): string {
  return (minor / 100).toFixed(2);
}

export function AnalyticsPage({ restaurantId }: Props) {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [hourly, setHourly] = useState<HourlyMetric[]>([]);
  const [topItems, setTopItems] = useState<TopItem[]>([]);
  const [avgCheck, setAvgCheck] = useState<PlatformAvgCheck[]>([]);
  const [daily, setDaily] = useState<DailyMetric[]>([]);
  const [reconciliation, setReconciliation] = useState<ReconciliationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportError, setExportError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [h, ti, ac, d, r] = await Promise.all([
        api.hourlyMetrics(restaurantId, days),
        api.topItems(restaurantId, days),
        api.avgCheck(restaurantId, days),
        api.daily(restaurantId),
        api.reconciliation(restaurantId, days),
      ]);
      setHourly(h);
      setTopItems(ti);
      setAvgCheck(ac);
      setDaily(d);
      setReconciliation(r);
    } catch {
      // Silently handle — user may not have data yet
    } finally {
      setLoading(false);
    }
  }, [restaurantId, days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Summary from daily data
  const totals = daily.reduce(
    (acc, m) => {
      acc.orders += m.orders_count;
      acc.revenue += m.revenue_minor;
      acc.cancelled += m.cancelled_count;
      return acc;
    },
    { orders: 0, revenue: 0, cancelled: 0 },
  );

  const handleExport = async () => {
    setExportError(null);
    try {
      await api.exportCsv(restaurantId, days);
    } catch (err: unknown) {
      setExportError(err instanceof Error ? err.message : String(err));
    }
  };

  const handlePdf = () => {
    window.print();
  };

  // Build full 24h array for chart
  const hourlyFull: HourlyMetric[] = Array.from({ length: 24 }, (_, i) => {
    const found = hourly.find((h) => h.hour === i);
    return found ?? { hour: i, orders_count: 0, revenue_minor: 0 };
  });
  const maxOrders = Math.max(...hourlyFull.map((h) => h.orders_count), 1);

  if (loading) {
    return (
      <div className="card">
        <div className="loading-spinner" />
        <p className="muted" style={{ textAlign: "center" }}>{t("generic.loading")}</p>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Period selector + export */}
      <div className="analytics-toolbar">
        <div className="analytics-period-selector">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              className={`btn-period ${days === d ? "btn-period-active" : ""}`}
              onClick={() => setDays(d)}
            >
              {d} {t("adv_analytics.days")}
            </button>
          ))}
        </div>
        <div className="analytics-export-btns">
          <button className="btn btn-secondary-sm" onClick={handleExport}>
            📥 {t("adv_analytics.export_csv")}
          </button>
          <button className="btn btn-secondary-sm" onClick={handlePdf}>
            🖨️ {t("adv_analytics.export_pdf")}
          </button>
        </div>
      </div>

      {exportError && (
        <p className="error" style={{ marginBottom: 12 }}>
          {t("adv_analytics.export_failed")}: {exportError}
        </p>
      )}

      {/* Summary cards */}
      <div className="analytics-summary-grid">
        <div className="analytics-summary-card">
          <div className="analytics-summary-icon">📦</div>
          <div className="analytics-summary-value">{totals.orders}</div>
          <div className="analytics-summary-label">{t("analytics.total")}</div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-icon">💰</div>
          <div className="analytics-summary-value">{fmt(totals.revenue)} PLN</div>
          <div className="analytics-summary-label">{t("analytics.revenue")}</div>
        </div>
        <div className="analytics-summary-card">
          <div className="analytics-summary-icon">📊</div>
          <div className="analytics-summary-value">
            {totals.orders > 0 ? fmt(totals.revenue / totals.orders) : "0.00"} PLN
          </div>
          <div className="analytics-summary-label">{t("analytics.avg")}</div>
        </div>
        <div className="analytics-summary-card analytics-summary-danger">
          <div className="analytics-summary-icon">❌</div>
          <div className="analytics-summary-value">{totals.cancelled}</div>
          <div className="analytics-summary-label">{t("analytics.cancelled")}</div>
        </div>
      </div>

      {/* Hourly heatmap chart */}
      <div className="card">
        <h3>{t("adv_analytics.hourly_title")}</h3>
        <div className="hourly-chart">
          {hourlyFull.map((h) => (
            <div key={h.hour} className="hourly-bar-wrapper">
              <div className="hourly-bar-value">{h.orders_count}</div>
              <div
                className="hourly-bar"
                style={{
                  height: `${(h.orders_count / maxOrders) * 100}%`,
                  backgroundColor: h.orders_count > maxOrders * 0.7
                    ? "var(--accent)"
                    : h.orders_count > maxOrders * 0.3
                    ? "rgba(0, 209, 178, 0.6)"
                    : "rgba(0, 209, 178, 0.25)",
                }}
                title={`${h.hour}:00 — ${h.orders_count} orders, ${fmt(h.revenue_minor)} PLN`}
              />
              <div className="hourly-bar-label">{h.hour}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Two-column layout: platform breakdown + avg check */}
      <div className="analytics-two-col">
        {/* Platform revenue */}
        <div className="card">
          <h3>{t("adv_analytics.platform_revenue")}</h3>
          {avgCheck.length === 0 ? (
            <p className="muted">{t("adv_analytics.no_data")}</p>
          ) : (
            <div className="platform-bars">
              {avgCheck.map((pc) => {
                const maxRev = Math.max(...avgCheck.map((r) => r.revenue_minor), 1);
                return (
                  <div key={pc.platform} className="platform-bar-row">
                    <div className="platform-bar-label">
                      {PLATFORM_LABELS[pc.platform] ?? pc.platform}
                    </div>
                    <div className="platform-bar-track">
                      <div
                        className="platform-bar-fill"
                        style={{
                          width: `${(pc.revenue_minor / maxRev) * 100}%`,
                          backgroundColor: PLATFORM_COLORS[pc.platform] ?? "var(--accent)",
                        }}
                      />
                    </div>
                    <div className="platform-bar-amount">{fmt(pc.revenue_minor)} PLN</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Avg check by platform */}
        <div className="card">
          <h3>{t("adv_analytics.avg_check_title")}</h3>
          {avgCheck.length === 0 ? (
            <p className="muted">{t("adv_analytics.no_data")}</p>
          ) : (
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>{t("adv_analytics.platform")}</th>
                  <th>{t("analytics.total")}</th>
                  <th>{t("adv_analytics.avg_check")}</th>
                </tr>
              </thead>
              <tbody>
                {avgCheck.map((pc) => (
                  <tr key={pc.platform}>
                    <td>
                      <span
                        className="platform-dot"
                        style={{ backgroundColor: PLATFORM_COLORS[pc.platform] ?? "var(--accent)" }}
                      />
                      {PLATFORM_LABELS[pc.platform] ?? pc.platform}
                    </td>
                    <td>{pc.orders_count}</td>
                    <td>{fmt(pc.avg_check_minor)} PLN</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Top dishes */}
      <div className="card">
        <h3>{t("adv_analytics.top_dishes_title")}</h3>
        {topItems.length === 0 ? (
          <p className="muted">{t("adv_analytics.no_data")}</p>
        ) : (
          <table className="analytics-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t("adv_analytics.dish_name")}</th>
                <th>{t("adv_analytics.quantity")}</th>
                <th>{t("analytics.revenue")}</th>
              </tr>
            </thead>
            <tbody>
              {topItems.map((item, i) => (
                <tr key={item.name}>
                  <td className="muted">{i + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.total_quantity}</td>
                  <td>{fmt(item.total_revenue_minor)} PLN</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reconciliation */}
      {reconciliation.length > 0 && (
        <div className="card">
          <h3>{t("adv_analytics.reconciliation_title")}</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>{t("adv_analytics.platform")}</th>
                <th>{t("analytics.total")}</th>
                <th>{t("analytics.revenue")}</th>
                <th>{t("adv_analytics.fees")}</th>
                <th>{t("adv_analytics.net")}</th>
              </tr>
            </thead>
            <tbody>
              {reconciliation.map((row) => (
                <tr key={row.platform}>
                  <td>
                    <span
                      className="platform-dot"
                      style={{ backgroundColor: PLATFORM_COLORS[row.platform] ?? "var(--accent)" }}
                    />
                    {PLATFORM_LABELS[row.platform] ?? row.platform}
                  </td>
                  <td>{row.orders_count}</td>
                  <td>{fmt(row.revenue_minor)} PLN</td>
                  <td className="text-danger">
                    -{fmt(row.delivery_fee_minor + row.service_fee_minor - row.tip_minor + row.discount_minor)} PLN
                  </td>
                  <td className="text-success">{fmt(row.net_minor)} PLN</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
